import { Component, OnInit, ViewEncapsulation, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { AssetApiService } from 'src/app/api/asset/assetApiService';
import { AssetSearchResult } from 'src/app/api/asset/search/assetSearchResult';
import { AssetSearchCriteria } from 'src/app/api/asset/search/assetSearchCriteria';
import { RecentSearchItem } from 'src/app/services/recent-searches/recent-search-item';
import { RecentSearchesService } from 'src/app/services/recent-searches/recent-searches.service';
import { SearchStateService } from 'src/app/services/search-state/search-state.service';
import { Const } from 'src/app/const';

@Component({
    selector: 'asset-search',
    templateUrl: './asset-search.component.html',
    styleUrls: ['./asset-search.component.css'],
    host: {'class': 'asset-search'},
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AssetSearchComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('loadingTrigger') loadingTrigger!: ElementRef;
    private observer: IntersectionObserver | null = null;
    searchCriteria: AssetSearchCriteria = new AssetSearchCriteria();
    assets: AssetSearchResult[] | null = null;
    loading: boolean = false;
    recentSearches: RecentSearchItem[] = [];
    moreDataAvailable: boolean = false;
    nextPage: number = 0;

    constructor(
        private assetApiService: AssetApiService,
        private recentSearchesService: RecentSearchesService,
        private searchStateService: SearchStateService
    ) { }

    get isAdvancedSearchUsed(): boolean {
        return !!(
            this.searchCriteria.customer ||
            this.searchCriteria.site ||
            this.searchCriteria.typeCode ||
            this.searchCriteria.orderNumber ||
            this.searchCriteria.country ||
            this.searchCriteria.city
        );
    }

    ngOnInit(): void {
        const savedState = this.searchStateService.getAssetSearchState();
        if (savedState) {
            this.searchCriteria = new AssetSearchCriteria(savedState);
            this.search();
        }

        this.recentSearchesService.recentSearchesObservable.subscribe(recentSearches => {
            this.recentSearches = recentSearches;
        });

        this.initializeIntersectionObserver();
    }

    private initializeIntersectionObserver(): void {
        this.observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !this.loading && this.moreDataAvailable) {
                    this.loadMoreAssets();
                }
            },
            { threshold: 0.5 }
        );
    }

    ngAfterViewInit(): void {
        if (!this.loadingTrigger) {
            console.warn('Loading trigger element not found in template');
            return;
        }

        if (this.loadingTrigger && this.observer) {
            this.observer.observe(this.loadingTrigger.nativeElement);
        }
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    search(): void {
        this.loading = true;
        this.assets = null;
        this.nextPage = 0;

        this.searchStateService.saveSearchState(this.searchCriteria);

        this.assetApiService
            .searchAsset(this.searchCriteria)
            .subscribe(assets => this.handleSearchResults(assets));
    }

    private handleSearchResults(assets: AssetSearchResult[]) {
        this.loading = false;
        this.assets = assets;
        this.nextPage += Const.SEARCH_QUERY_LIMIT;
        this.moreDataAvailable = assets.length == Const.SEARCH_QUERY_LIMIT;

        if (this.assets?.length > 0 && this.searchCriteria.serialNumber) {
            this.recentSearchesService.saveRecentSearch(
                this.searchCriteria.serialNumber,
                this.assets[0].assetId
            );
        }
    }

    loadMoreAssets(): void {
        this.loading = true;

        this.assetApiService
            .searchAsset(this.searchCriteria, this.nextPage)
            .subscribe(assets => this.handleLoadMoreResults(assets));
    }

    private handleLoadMoreResults(assets: AssetSearchResult[]) {
        this.loading = false;
        this.assets = this.assets!.concat(assets);
        this.nextPage += Const.SEARCH_QUERY_LIMIT;
        this.moreDataAvailable = assets.length == Const.SEARCH_QUERY_LIMIT;
    }

    isEmptySearch(): boolean {
        return Object.values(this.searchCriteria).every(value => !value);
    }

    openRecentSearch(searchTerm: string): void {
        this.searchCriteria = new AssetSearchCriteria({ serialNumber: searchTerm });
        this.search();
    }
}
